/** Privacy store — controls visibility of `owner_only` transactions.
 *
 * Two modes:
 *  - "protected" (default): backend hides owner_only transactions.
 *  - "private": owner unlocked the layer (PIN verified) — backend returns all.
 *
 * Mode is held only in memory + sessionStorage. Auto-locks after 5 min idle.
 */

const IDLE_MS = 5 * 60 * 1000;
const SS_KEY = "takite.privacy.mode";
const SS_EXPIRES = "takite.privacy.expires";

export type PrivacyMode = "protected" | "private";

function readInitial(): PrivacyMode {
  try {
    const m = sessionStorage.getItem(SS_KEY);
    const exp = Number(sessionStorage.getItem(SS_EXPIRES) || 0);
    if (m === "private" && exp > Date.now()) return "private";
  } catch {}
  return "protected";
}

function createPrivacy() {
  let mode = $state<PrivacyMode>(readInitial());
  let pinVerifying = $state(false);
  let timer: ReturnType<typeof setTimeout> | null = null;

  function persist() {
    try {
      if (mode === "private") {
        sessionStorage.setItem(SS_KEY, mode);
        sessionStorage.setItem(SS_EXPIRES, String(Date.now() + IDLE_MS));
      } else {
        sessionStorage.removeItem(SS_KEY);
        sessionStorage.removeItem(SS_EXPIRES);
      }
    } catch {}
  }

  function arm() {
    if (timer) clearTimeout(timer);
    if (mode !== "private") return;
    timer = setTimeout(() => lock(), IDLE_MS);
    persist();
  }

  function nudge() {
    if (mode === "private") arm();
  }

  function unlockNoPin() {
    mode = "private";
    arm();
  }

  function lock() {
    mode = "protected";
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    persist();
    try {
      window.dispatchEvent(new CustomEvent("takite:privacy-locked"));
    } catch {}
  }

  // Idle listeners (lazy install once)
  if (typeof window !== "undefined") {
    const idle = () => nudge();
    window.addEventListener("pointerdown", idle, { passive: true });
    window.addEventListener("keydown", idle, { passive: true });
    window.addEventListener("focus", idle);
  }

  return {
    get mode() { return mode; },
    get isPrivate() { return mode === "private"; },
    get pinVerifying() { return pinVerifying; },
    set pinVerifying(v: boolean) { pinVerifying = v; },
    unlock: unlockNoPin,
    lock,
    nudge,
  };
}

export const privacy = createPrivacy();
